import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Slider } from "@/components/ui/slider";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Landmark, Save, Loader2, ShieldCheck, AlertTriangle, Wifi, WifiOff,
  Activity, Settings2, DollarSign, Zap, Radio, Server, Key, TestTube,
  CheckCircle2, XCircle, Info, ChevronRight,
} from "lucide-react";
import { useState, useEffect } from "react";
import { toast } from "sonner";

// 行情源选项
const QUOTE_SOURCES = [
  { value: "tencent", label: "腾讯财经 L1（免费）", desc: "免费实时行情，无需配置，延迟约3秒，五档盘口", badge: "免费", badgeColor: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10" },
  { value: "xtquant", label: "XtQuant L1（QMT内置）", desc: "兴业/海通 QMT 内置行情，五档盘口，延迟约3秒", badge: "当前", badgeColor: "text-blue-400 border-blue-500/30 bg-blue-500/10" },
  { value: "nats_l2", label: "NATS L2 逐笔（第三方）", desc: "base32.cn 逐笔成交+合成订单簿，毫秒级延迟，¥150/月起", badge: "推荐", badgeColor: "text-purple-400 border-purple-500/30 bg-purple-500/10" },
  { value: "eastmoney_l2", label: "东方财富 Level-2", desc: "十档实时盘口、逐笔成交，需申请权限", badge: "付费", badgeColor: "text-amber-400 border-amber-500/30 bg-amber-500/10" },
];

// 券商连接方式（此处只配置用哪个渠道连接，不控制是否实盘）
// 是否真实下单由「参数配置页 → 交易模式控制」统一管理
const TRADE_MODES = [
  { value: "smtq", label: "兴业证券 SMT-Q", desc: "通过兴业SMT-Q（QMT体系）+ Windows交易代理服务器连接", badge: "推荐", badgeColor: "text-blue-400 border-blue-500/30 bg-blue-500/10" },
  { value: "emt", label: "东方财富 EMT", desc: "通过EMT量化交易终端连接", badge: "待开通", badgeColor: "text-amber-400 border-amber-500/30 bg-amber-500/10" },
  { value: "qmt", label: "东方财富 QMT/掘金", desc: "通过QMT（掘金量化）终端连接", badge: "待开通", badgeColor: "text-amber-400 border-amber-500/30 bg-amber-500/10" },
];

// 挂单策略选项
const ORDER_STRATEGIES = [
  { value: "counterparty", label: "对手价", desc: "买入挂卖一价，卖出挂买一价" },
  { value: "counterparty_plus1", label: "对手价+1档", desc: "买入挂卖二价，几乎100%成交" },
  { value: "weighted_mid", label: "盘口加权中间价", desc: "根据买卖盘口量加权计算" },
  { value: "smart_chase", label: "智能追单", desc: "首挂对手价，未成交自动追价" },
];

export default function Broker() {
  // 读取 URL ?tab= 参数，支持从其他页面跳转直接激活指定 tab
  const [activeConfigTab, setActiveConfigTab] = useState(() => {
    if (typeof window !== "undefined") {
      const p = new URLSearchParams(window.location.search);
      return p.get("tab") || "quote";
    }
    return "quote";
  });

  // 券商账号
  const { data: account, isLoading: accountLoading, refetch: refetchAccount } = trpc.broker.get.useQuery();
  const saveBroker = trpc.broker.save.useMutation({
    onSuccess: () => { toast.success("券商账号已保存"); refetchAccount(); },
    onError: (err: any) => toast.error(err.message),
  });

  // 接口配置
  const { data: ifConfig, isLoading: configLoading, refetch: refetchConfig } = trpc.interfaceConfig.get.useQuery();
  const saveConfig = trpc.interfaceConfig.save.useMutation({
    onSuccess: () => { toast.success("接口配置已保存"); refetchConfig(); },
    onError: (err: any) => toast.error(err.message),
  });
  const testConn = trpc.interfaceConfig.testConnection.useMutation({
    onSuccess: (res) => {
      if (res.success) toast.success(res.message);
      else toast.error(res.message);
    },
    onError: (err: any) => toast.error(err.message),
  });

  // 券商账号状态
  const [accountId, setAccountId] = useState("");
  const [password, setPassword] = useState("");
  const [isActive, setIsActive] = useState(false);

  // 接口配置状态
  const [quoteSource, setQuoteSource] = useState("tencent");
  const [l2ApiKey, setL2ApiKey] = useState("");
  const [l2ApiUrl, setL2ApiUrl] = useState("");
  const [natsUsername, setNatsUsername] = useState("");
  const [natsPassword, setNatsPassword] = useState("");
  const [xingyeAccount, setXingyeAccount] = useState("");
  const [haitongAccount, setHaitongAccount] = useState("");
  const [haitongQmtPath, setHaitongQmtPath] = useState("");
  const [quoteRefreshMs, setQuoteRefreshMs] = useState(3000);
  const [engineTickMs, setEngineTickMs] = useState(5000);
  const [tradeMode, setTradeMode] = useState("simulation");
  const configInitializedRef = useRef(false);  // 用 ref 而非 state，避免异步更新导致重复初始化
  const [tradeSdkUrl, setTradeSdkUrl] = useState("");
  const [tradeSdkKey, setTradeSdkKey] = useState("");
  const [commissionRate, setCommissionRate] = useState(2.5);
  const [minCommission, setMinCommission] = useState(5);
  const [stampTaxRate, setStampTaxRate] = useState(0.5);
  const [transferFeeRate, setTransferFeeRate] = useState(0.2);
  const [defaultOrderStrategy, setDefaultOrderStrategy] = useState("counterparty");
  // 兴业SMT-Q交易代理配置
  const [tradingProxyUrl, setTradingProxyUrl] = useState("");
  const [tradingProxyApiKey, setTradingProxyApiKey] = useState("");
  const [xyAccountId, setXyAccountId] = useState("");
  const [miniQmtPath, setMiniQmtPath] = useState("");
  const [tradingProxyMode, setTradingProxyMode] = useState("sim");
  const [showLiveConfirm, setShowLiveConfirm] = useState(false);
  const [pendingMode, setPendingMode] = useState<string | null>(null);
  const [hasExplicitlyChangedMode, setHasExplicitlyChangedMode] = useState(false);
  const [enableL2, setEnableL2] = useState(false);

  // 初始化券商账号
  useEffect(() => {
    if (account) {
      setAccountId(account.accountId || "");
      setIsActive(account.isActive === 1);
    }
  }, [account]);

  // 初始化接口配置
  useEffect(() => {
    if (ifConfig) {
      setQuoteSource(ifConfig.quoteSource);
      setL2ApiKey(ifConfig.l2ApiKey);
      setL2ApiUrl(ifConfig.l2ApiUrl);
      setQuoteRefreshMs(ifConfig.quoteRefreshMs);
      setEngineTickMs(ifConfig.engineTickMs);
      if (!configInitializedRef.current) setTradeMode(ifConfig.tradeMode);
      setTradeSdkUrl(ifConfig.tradeSdkUrl);
      setTradeSdkKey(ifConfig.tradeSdkKey);
      setCommissionRate(ifConfig.commissionRate);
      setMinCommission(ifConfig.minCommission);
      setStampTaxRate(ifConfig.stampTaxRate);
      setTransferFeeRate(ifConfig.transferFeeRate);
      setDefaultOrderStrategy(ifConfig.defaultOrderStrategy);
      // SMT-Q配置
      setTradingProxyUrl(ifConfig.tradingProxyUrl || "");
      setTradingProxyApiKey(ifConfig.tradingProxyApiKey || "");
      setXyAccountId(ifConfig.xyAccountId || "");
      setMiniQmtPath(ifConfig.miniQmtPath || "");
      setTradingProxyMode(ifConfig.tradingProxyMode || "sim");
      setEnableL2(!!ifConfig.enableL2);
      // NATS L2 参数
      setNatsUsername((ifConfig as any).natsUsername || "");
      setNatsPassword((ifConfig as any).natsPassword || "");
      // NATS L2 参数
      setNatsUsername((ifConfig as any).natsUsername || "");
      setNatsPassword((ifConfig as any).natsPassword || "");
      configInitializedRef.current = true;  // 同步赋值，不触发 re-render
    }
  }, [ifConfig]);

  const handleSaveBroker = () => {
    if (!accountId) { toast.error("请输入账号"); return; }
    saveBroker.mutate({ accountId, password: password || "unchanged", isActive: isActive ? 1 : 0 });
  };

  const handleSaveQuoteConfig = () => {
    saveConfig.mutate({
      quoteSource: quoteSource as any,
      l2ApiKey,
      l2ApiUrl,
      quoteRefreshMs,
      engineTickMs,
      natsUsername,
      natsPassword,
    } as any);
  };

  const handleSaveTradeConfig = () => {
    // [Fix] 接口配置页不再写 tradeMode，防止覆盖参数配置页的交易模式设置
    // tradeMode 的唯一控制入口是「参数配置页 → 交易模式控制」
    const payload: Record<string, any> = {};
    if (tradeMode === 'smtq') {
      payload.tradingProxyUrl = tradingProxyUrl;
      payload.tradingProxyApiKey = tradingProxyApiKey;
      payload.xyAccountId = xyAccountId;
      payload.miniQmtPath = miniQmtPath;
      // P0 Fix 20260421: 只有用户显式切换过模式才提交 tradingProxyMode
      // 否则可能把 DB 里历史的 live 配置意外写回, 触发 proxy 自残循环
      if (hasExplicitlyChangedMode) {
        payload.tradingProxyMode = tradingProxyMode as any;
      }
      payload.enableL2 = enableL2;
    } else if (tradeMode === 'emt' || tradeMode === 'qmt') {
      payload.tradeSdkUrl = tradeSdkUrl;
      payload.tradeSdkKey = tradeSdkKey;
    }
    // [Fix] brokerType 字段记录连接方式，独立于 tradeMode
    payload.brokerType = tradeMode;
    saveConfig.mutate(payload);
  };

  const handleSaveFeeConfig = () => {
    saveConfig.mutate({
      commissionRate,
      minCommission,
      stampTaxRate,
      transferFeeRate,
      defaultOrderStrategy: defaultOrderStrategy as any,
    });
  };

  const isLoading = accountLoading || configLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div>
        <h1 className="text-xl font-bold flex items-center gap-2">
          <Settings2 className="h-5 w-5 text-primary" />
          接口配置中心
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          管理行情数据源、交易接口、佣金费率等核心配置
        </p>
      </div>

      {/* 状态概览 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card className="bg-card border-border/50">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/10">
              <Radio className="h-4 w-4 text-blue-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-muted-foreground">行情数据源</p>
              <p className="text-sm font-medium truncate">
                {QUOTE_SOURCES.find(s => s.value === quoteSource)?.label || quoteSource}
              </p>
            </div>
            {quoteSource === "tencent" ? (
              <Badge variant="outline" className="text-[10px] text-emerald-400 border-emerald-500/30 bg-emerald-500/10 shrink-0">
                <Wifi className="h-2.5 w-2.5 mr-1" />可用
              </Badge>
            ) : (
              <Badge variant="outline" className="text-[10px] text-amber-400 border-amber-500/30 bg-amber-500/10 shrink-0">
                {l2ApiKey ? "已配置" : "待配置"}
              </Badge>
            )}
          </CardContent>
        </Card>

        <Card className="bg-card border-border/50">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-500/10">
              <Zap className="h-4 w-4 text-purple-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-muted-foreground">交易接口</p>
              <p className="text-sm font-medium truncate">
                {TRADE_MODES.find(m => m.value === tradeMode)?.label || tradeMode}
              </p>
            </div>
            {tradeMode === "simulation" ? (
              <Badge variant="outline" className="text-[10px] text-emerald-400 border-emerald-500/30 bg-emerald-500/10 shrink-0">
                <ShieldCheck className="h-2.5 w-2.5 mr-1" />模拟
              </Badge>
            ) : (
              <Badge variant="outline" className="text-[10px] text-red-400 border-red-500/30 bg-red-500/10 shrink-0">
                <AlertTriangle className="h-2.5 w-2.5 mr-1" />实盘
              </Badge>
            )}
          </CardContent>
        </Card>

        <Card className="bg-card border-border/50">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-500/10">
              <DollarSign className="h-4 w-4 text-amber-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-muted-foreground">佣金费率</p>
              <p className="text-sm font-medium">
                万{commissionRate}（最低{minCommission}元）
              </p>
            </div>
            <Badge variant="outline" className="text-[10px] shrink-0">
              印花税 ‰{stampTaxRate}
            </Badge>
          </CardContent>
        </Card>
      </div>

      {/* 配置选项卡 */}
      <Tabs value={activeConfigTab} onValueChange={setActiveConfigTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-4 h-9">
          <TabsTrigger value="quote" className="text-xs">
            <Radio className="h-3 w-3 mr-1.5" />行情源
          </TabsTrigger>
          <TabsTrigger value="trade" className="text-xs">
            <Zap className="h-3 w-3 mr-1.5" />交易接口
          </TabsTrigger>
          <TabsTrigger value="fee" className="text-xs">
            <DollarSign className="h-3 w-3 mr-1.5" />费率
          </TabsTrigger>
          <TabsTrigger value="account" className="text-xs">
            <Landmark className="h-3 w-3 mr-1.5" />券商账号
          </TabsTrigger>
        </TabsList>

        {/* ===== 行情数据源配置 ===== */}
        <TabsContent value="quote" className="space-y-4">
          <Card className="bg-card border-border/50">
            <CardHeader>
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Radio className="h-4 w-4 text-blue-400" />
                行情数据源选择
              </CardTitle>
              <CardDescription className="text-xs">
                选择行情数据来源，不同数据源在延迟、精度和费用上有差异
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {QUOTE_SOURCES.map((src) => (
                <div
                  key={src.value}
                  onClick={() => setQuoteSource(src.value)}
                  className={`p-4 rounded-lg border cursor-pointer transition-all ${
                    quoteSource === src.value
                      ? "border-primary/50 bg-primary/5"
                      : "border-border/50 hover:border-border"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full border-2 ${
                        quoteSource === src.value ? "border-primary bg-primary" : "border-muted-foreground/30"
                      }`} />
                      <div>
                        <p className="text-sm font-medium">{src.label}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{src.desc}</p>
                      </div>
                    </div>
                    <Badge variant="outline" className={`text-[10px] ${src.badgeColor}`}>
                      {src.badge}
                    </Badge>
                  </div>
                </div>
              ))}

              {/* NATS L2 配置（仅在选择 NATS L2 时显示） */}
              {quoteSource === "nats_l2" && (
                <div className="space-y-3 p-4 rounded-lg bg-purple-500/5 border border-purple-500/20">
                  <p className="text-xs font-medium text-purple-400 flex items-center gap-1.5">
                    <Zap className="h-3 w-3" /> NATS L2 逐笔行情配置（base32.cn）
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">服务器地址</Label>
                      <Input value="43.143.73.95" readOnly className="h-9 text-xs bg-muted/30" placeholder="43.143.73.95（上海）" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">端口</Label>
                      <Input value="31886" readOnly className="h-9 text-xs bg-muted/30" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">用户名</Label>
                      <Input
                        value={natsUsername}
                        onChange={(e) => setNatsUsername(e.target.value)}
                        placeholder="购买后获取"
                        className="h-9 text-xs"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">密码</Label>
                      <Input
                        value={natsPassword}
                        onChange={(e) => setNatsPassword(e.target.value)}
                        placeholder="购买后获取"
                        className="h-9 text-xs"
                        type="password"
                      />
                    </div>
                  </div>
                  <div className="flex items-start gap-2 mt-1">
                    <Info className="h-3.5 w-3.5 text-purple-400 mt-0.5 shrink-0" />
                    <p className="text-[11px] text-muted-foreground">
                      逐笔成交¥150/月，测试账号：level2_test / level2@test（仅支持600519/300750）。
                      购买地址：<span className="text-purple-400">淘宝搜「沪深Level2逐笔」</span>
                    </p>
                  </div>
                </div>
              )}

              {/* Level-2 配置（仅在选择东方财富Level-2时显示） */}
              {quoteSource === "eastmoney_l2" && (
                <div className="space-y-3 p-4 rounded-lg bg-muted/30 border border-border/30">
                  <p className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                    <Key className="h-3 w-3" /> Level-2 接口配置
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">API Key</Label>
                      <Input
                        value={l2ApiKey}
                        onChange={(e) => setL2ApiKey(e.target.value)}
                        placeholder="请输入东方财富 Level-2 API Key"
                        className="h-9 text-xs"
                        type="password"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">服务器地址</Label>
                      <Input
                        value={l2ApiUrl}
                        onChange={(e) => setL2ApiUrl(e.target.value)}
                        placeholder="例如: wss://l2.eastmoney.com/api"
                        className="h-9 text-xs"
                      />
                    </div>
                  </div>
                  <div className="flex items-start gap-2 mt-2">
                    <Info className="h-3.5 w-3.5 text-blue-400 mt-0.5 shrink-0" />
                    <p className="text-[11px] text-muted-foreground">
                      需要在东方财富官网申请 Level-2 行情权限。申请地址：
                      <span className="text-blue-400"> quote.eastmoney.com</span>
                    </p>
                  </div>
                </div>
              )}

              <Separator />

              {/* 刷新频率 */}
              <div className="space-y-4">
                <p className="text-xs font-medium text-muted-foreground">刷新频率设置</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs">SSE 行情推送间隔</Label>
                      <span className="text-xs text-muted-foreground">{(quoteRefreshMs / 1000).toFixed(1)}秒</span>
                    </div>
                    <Slider
                      value={[quoteRefreshMs]}
                      onValueChange={([v]) => setQuoteRefreshMs(v)}
                      min={1000}
                      max={30000}
                      step={500}
                      className="w-full"
                    />
                    <p className="text-[10px] text-muted-foreground">控制前端实时行情的刷新频率</p>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs">交易引擎 Tick 间隔</Label>
                      <span className="text-xs text-muted-foreground">{(engineTickMs / 1000).toFixed(1)}秒</span>
                    </div>
                    <Slider
                      value={[engineTickMs]}
                      onValueChange={([v]) => setEngineTickMs(v)}
                      min={1000}
                      max={30000}
                      step={500}
                      className="w-full"
                    />
                    <p className="text-[10px] text-muted-foreground">控制交易Agent获取行情并决策的频率</p>
                  </div>
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <Button onClick={handleSaveQuoteConfig} disabled={saveConfig.isPending} className="flex-1">
                  {saveConfig.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  保存行情配置
                </Button>
                <Button
                  variant="outline"
                  onClick={() => testConn.mutate({ type: "quote" })}
                  disabled={testConn.isPending}
                >
                  {testConn.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <TestTube className="h-3.5 w-3.5" />}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== 交易接口配置 ===== */}
        <TabsContent value="trade" className="space-y-4">
          {/* 引擎重启后确认交易模式 - 问题1 */}
          {(() => {
            const dbMode = (ifConfig as any)?.tradeMode || "simulation";
            const modeMap: Record<string, {label: string; cls: string; desc: string}> = {
              simulation: { label: "模拟交易", cls: "border-blue-500/30 bg-blue-500/5 text-blue-400", desc: "用真实L2行情+固定10000股底仓做虚拟T+0，绝不下单。" },
              smtq:       { label: "⚡ 实盘交易", cls: "border-red-500/30 bg-red-500/5 text-red-400", desc: "真实下单模式！引擎重启后需在此页面重新确认当前模式是否正确。" },
            };
            const m = modeMap[dbMode] || modeMap["simulation"];
            return (
              <Card className={"border " + m.cls.split(" ")[0] + " " + m.cls.split(" ")[1]}>
                <CardContent className="p-3 flex items-center gap-3">
                  <div className={"text-xs font-bold px-2 py-1 rounded border " + m.cls}>{m.label}</div>
                  <div>
                    <p className="text-xs font-medium">当前交易模式（参数配置页统一管理）</p>
                    <p className="text-[10px] text-muted-foreground">{m.desc}</p>
                    <a href="/config" className="text-[10px] text-blue-400 underline">前往参数配置修改 →</a>
                  </div>
                </CardContent>
              </Card>
            );
          })()}
          {/* 安全提示：统一导向参数配置页 */}
          <Card className="border-blue-500/20 bg-blue-500/5">
            <CardContent className="p-3 flex items-center gap-3">
              <Info className="h-4 w-4 text-blue-400 shrink-0" />
              <div className="text-xs text-muted-foreground">
                此页面只配置<span className="text-blue-400 font-medium">连接哪家券商</span>（SDK/代理地址、账号等）。
                是否真实下单请前往
                <a href="/config" className="text-blue-400 underline mx-1">参数配置 → 交易模式控制</a>
                设置（模拟 / 纯实盘）。
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border/50">
            <CardHeader>
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Zap className="h-4 w-4 text-purple-400" />
                券商连接方式
              </CardTitle>
              <CardDescription className="text-xs">
                选择连接哪家券商的交易通道。
                <span className="text-amber-400 font-medium"> ⚠️ 是否真实下单由「参数配置页 → 交易模式控制」统一管理，与此处无关。</span>
                <a href="/config" className="text-blue-400 underline ml-1">前往参数配置设置交易模式 →</a>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {TRADE_MODES.map((mode) => (
                <div
                  key={mode.value}
                  onClick={() => setTradeMode(mode.value)}
                  className={`p-4 rounded-lg border cursor-pointer transition-all ${
                    tradeMode === mode.value
                      ? "border-primary/50 bg-primary/5"
                      : "border-border/50 hover:border-border"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full border-2 ${
                        tradeMode === mode.value ? "border-primary bg-primary" : "border-muted-foreground/30"
                      }`} />
                      <div>
                        <p className="text-sm font-medium">{mode.label}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{mode.desc}</p>
                      </div>
                    </div>
                    <Badge variant="outline" className={`text-[10px] ${mode.badgeColor}`}>
                      {mode.badge}
                    </Badge>
                  </div>
                </div>
              ))}

              {/* 兴业SMT-Q 交易代理配置 */}
              {tradeMode === "smtq" && (
                <div className="space-y-4 p-4 rounded-lg bg-blue-500/5 border border-blue-500/20">
                  <p className="text-xs font-medium text-blue-400 flex items-center gap-1.5">
                    <Server className="h-3 w-3" /> 兴业 SMT-Q 交易代理配置
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">交易代理服务器地址 *</Label>
                      <Input
                        value={tradingProxyUrl}
                        onChange={(e) => setTradingProxyUrl(e.target.value)}
                        placeholder="例如: http://your-server-ip:8888"
                        className="h-9 text-xs"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">交易代理 API Key</Label>
                      <Input
                        value={tradingProxyApiKey}
                        onChange={(e) => setTradingProxyApiKey(e.target.value)}
                        placeholder="用于验证交易代理服务器身份"
                        className="h-9 text-xs"
                        type="password"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">兴业资金账号</Label>
                      <div className="flex gap-2">
                        <Input
                          value={xyAccountId}
                          onChange={(e) => setXyAccountId(e.target.value)}
                          placeholder="开通 SMT-Q 后获取的资金账号"
                          className="h-9 text-xs flex-1"
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-9 text-xs shrink-0"
                          disabled={saveConfig.isPending}
                          onClick={() => saveConfig.mutate({ xyAccountId, miniQmtPath } as any)}
                        >
                          {saveConfig.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3 mr-1" />}
                          保存账号
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">miniQMT 安装路径</Label>
                      <Input
                        value={miniQmtPath}
                        onChange={(e) => setMiniQmtPath(e.target.value)}
                        placeholder="例如: D:\\SMT-Q\\bin.x64\\XtMiniQmt.exe"
                        className="h-9 text-xs"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">交易模式</Label>
                      <Select value={tradingProxyMode} onValueChange={(val) => {
                        if (val !== tradingProxyMode) {
                          setPendingMode(val);
                          setShowLiveConfirm(true);
                        }
                      }}>
                        <SelectTrigger className="h-9 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="sim">模拟交易（安全测试）</SelectItem>
                          <SelectItem value="live">实盘交易（谨慎操作）</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between p-2 rounded-lg bg-muted/30">
                        <div>
                          <Label className="text-xs">行情等级</Label>
                          <p className="text-[10px] text-muted-foreground">
                            "L1 五档盘口（SMT-Q 当前仅支持 L1）"
                          </p>
                        </div>
                        <Switch checked={enableL2} onCheckedChange={setEnableL2} />
                      </div>
                      {!enableL2 && (
                        <p className="text-[10px] text-muted-foreground px-2">当前使用 L1 五档行情，足以支撑 T+0 策略运行。如需升级到 L2 十档/逐笔，需联系讯投购买权限后开启。</p>
                      )}
                      {enableL2 && (
                        <div className="flex items-start gap-2 p-2 rounded bg-amber-500/10 border border-amber-500/20">
                          <AlertTriangle className="h-3.5 w-3.5 text-amber-400 mt-0.5 shrink-0" />
                          <p className="text-[10px] text-amber-300">L2 行情需要向讯投单独购买权限，请确认已开通后再启用，否则订阅会失败。</p>
                        </div>
                      )}
                    </div>
                  </div>
                  {tradingProxyMode === "live" && (
                    <div className="flex items-start gap-2 p-2 rounded bg-red-500/10 border border-red-500/20">
                      <AlertTriangle className="h-3.5 w-3.5 text-red-400 mt-0.5 shrink-0" />
                      <p className="text-[11px] text-red-300">实盘模式下所有交易指令将真实执行，请确保已充分测试策略并配置好风控参数。</p>
                    </div>
                  )}
                  <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/20 space-y-2">
                    <p className="text-xs font-medium text-emerald-400 flex items-center gap-1.5">
                      <Zap className="h-3 w-3" /> T+0 引擎运行模式
                    </p>
                    <p className="text-[11px] text-muted-foreground">
                      配置 SMT-Q 交易代理后，T+0 引擎将优先在 <span className="text-emerald-400 font-medium">Python 交易引擎端（国内服务器）</span>运行，
                      行情获取、信号计算、下单执行全程在国内完成，延迟极低。
                    </p>
                    <p className="text-[11px] text-muted-foreground">
                      当 Python 引擎不可用时，自动降级到 Web 端引擎（通过腐讯行情API + 交易代理转发）。
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <Info className="h-3.5 w-3.5 text-blue-400 mt-0.5 shrink-0" />
                    <div className="text-[11px] text-muted-foreground space-y-1">
                      <p>SMT-Q 是兴业证券基于迅投 QMT 体系的策略交易终端，支持 Python API（XtQuant）。</p>
                      <p>需要在阿里云 Windows 服务器上部署交易代理服务，并安装 miniQMT 客户端。</p>
                      <p>准入条件：总资产 ≥ 100万，联系兴业客户经理申请开通。</p>
                      <p>交易引擎支持自动更新，当Web端有新版本发布时，Python引擎会自动拉取并应用更新。</p>
                    </div>
                  </div>
                </div>
              )}

              {/* EMT/QMT SDK 配置 */}
              {(tradeMode === "emt" || tradeMode === "qmt") && (
                <div className="space-y-3 p-4 rounded-lg bg-muted/30 border border-border/30">
                  <p className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                    <Server className="h-3 w-3" /> {tradeMode.toUpperCase()} SDK 配置
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">SDK 服务器地址</Label>
                      <Input
                        value={tradeSdkUrl}
                        onChange={(e) => setTradeSdkUrl(e.target.value)}
                        placeholder={tradeMode === "emt" ? "例如: tcp://127.0.0.1:6001" : "例如: tcp://127.0.0.1:7001"}
                        className="h-9 text-xs"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">API Key</Label>
                      <Input
                        value={tradeSdkKey}
                        onChange={(e) => setTradeSdkKey(e.target.value)}
                        placeholder={`请输入 ${tradeMode.toUpperCase()} API Key`}
                        className="h-9 text-xs"
                        type="password"
                      />
                    </div>
                  </div>
                  <div className="flex items-start gap-2 mt-2">
                    <Info className="h-3.5 w-3.5 text-blue-400 mt-0.5 shrink-0" />
                    <div className="text-[11px] text-muted-foreground space-y-1">
                      {tradeMode === "emt" ? (
                        <>
                          <p>EMT（Extreme Market Trading）是东方财富的高速交易终端。</p>
                          <p>需要在本地安装 EMT 客户端并开启 API 服务。申请地址：<span className="text-blue-400"> emt.18.cn</span></p>
                        </>
                      ) : (
                        <>
                          <p>QMT（掘金量化）是东方财富的量化交易平台。</p>
                          <p>需要在本地安装 QMT 客户端并开启 API 服务。申请地址：<span className="text-blue-400"> qmt.eastmoney.com</span></p>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <Button onClick={handleSaveTradeConfig} disabled={saveConfig.isPending} className="flex-1">
                  {saveConfig.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  保存交易配置
                </Button>
                <Button
                  variant="outline"
                  onClick={() => testConn.mutate({ type: "trade" })}
                  disabled={testConn.isPending}
                >
                  {testConn.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <TestTube className="h-3.5 w-3.5" />}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== 佣金费率配置 ===== */}
        <TabsContent value="fee" className="space-y-4">
          <Card className="bg-card border-border/50">
            <CardHeader>
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-amber-400" />
                交易费率配置
              </CardTitle>
              <CardDescription className="text-xs">
                根据您的券商佣金标准自定义费率，影响交易成本计算和盈亏统计
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* 佣金率 */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-xs font-medium">佣金率</Label>
                    <p className="text-[10px] text-muted-foreground mt-0.5">买入和卖出均收取</p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-muted-foreground">万</span>
                    <Input
                      type="number"
                      value={commissionRate}
                      onChange={(e) => setCommissionRate(parseFloat(e.target.value) || 0)}
                      className="h-8 w-20 text-xs text-right"
                      step={0.1}
                      min={0}
                      max={100}
                    />
                  </div>
                </div>
                <Slider
                  value={[commissionRate]}
                  onValueChange={([v]) => setCommissionRate(+v.toFixed(1))}
                  min={0}
                  max={10}
                  step={0.1}
                  className="w-full"
                />
                <p className="text-[10px] text-muted-foreground">
                  即 {(commissionRate / 10000 * 100).toFixed(4)}%，10万元交易佣金约 ¥{Math.max((100000 * commissionRate / 10000), minCommission).toFixed(2)}
                </p>
              </div>

              <Separator />

              {/* 最低佣金 */}
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-xs font-medium">最低佣金</Label>
                  <p className="text-[10px] text-muted-foreground mt-0.5">单笔交易佣金不低于此金额</p>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-muted-foreground">¥</span>
                  <Input
                    type="number"
                    value={minCommission}
                    onChange={(e) => setMinCommission(parseFloat(e.target.value) || 0)}
                    className="h-8 w-20 text-xs text-right"
                    step={1}
                    min={0}
                  />
                </div>
              </div>

              <Separator />

              {/* 印花税 */}
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-xs font-medium">印花税率</Label>
                  <p className="text-[10px] text-muted-foreground mt-0.5">仅卖出时收取（2023年8月起减半至千分之0.5）</p>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-muted-foreground">‰</span>
                  <Input
                    type="number"
                    value={stampTaxRate}
                    onChange={(e) => setStampTaxRate(parseFloat(e.target.value) || 0)}
                    className="h-8 w-20 text-xs text-right"
                    step={0.1}
                    min={0}
                  />
                </div>
              </div>

              <Separator />

              {/* 过户费 */}
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-xs font-medium">过户费率</Label>
                  <p className="text-[10px] text-muted-foreground mt-0.5">沪深两市均收取</p>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-muted-foreground">万</span>
                  <Input
                    type="number"
                    value={transferFeeRate}
                    onChange={(e) => setTransferFeeRate(parseFloat(e.target.value) || 0)}
                    className="h-8 w-20 text-xs text-right"
                    step={0.1}
                    min={0}
                  />
                </div>
              </div>

              <Separator />

              {/* 默认挂单策略 */}
              <div className="space-y-3">
                <Label className="text-xs font-medium">默认挂单策略</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {ORDER_STRATEGIES.map((s) => (
                    <div
                      key={s.value}
                      onClick={() => setDefaultOrderStrategy(s.value)}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        defaultOrderStrategy === s.value
                          ? "border-primary/50 bg-primary/5"
                          : "border-border/50 hover:border-border"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-2.5 h-2.5 rounded-full border-2 ${
                          defaultOrderStrategy === s.value ? "border-primary bg-primary" : "border-muted-foreground/30"
                        }`} />
                        <div>
                          <p className="text-xs font-medium">{s.label}</p>
                          <p className="text-[10px] text-muted-foreground">{s.desc}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* 费用预览 */}
              <Card className="bg-muted/30 border-border/30">
                <CardContent className="p-3">
                  <p className="text-xs font-medium mb-2">费用预览（以10万元交易为例）</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
                    <div>
                      <p className="text-muted-foreground">买入佣金</p>
                      <p className="font-medium">¥{Math.max(100000 * commissionRate / 10000, minCommission).toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">卖出佣金</p>
                      <p className="font-medium">¥{Math.max(100000 * commissionRate / 10000, minCommission).toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">印花税（卖出）</p>
                      <p className="font-medium">¥{(100000 * stampTaxRate / 1000).toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">过户费</p>
                      <p className="font-medium">¥{(100000 * transferFeeRate / 10000).toFixed(2)}</p>
                    </div>
                  </div>
                  <Separator className="my-2" />
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">单次买卖总费用</span>
                    <span className="font-bold text-amber-400">
                      ¥{(
                        Math.max(100000 * commissionRate / 10000, minCommission) * 2 +
                        100000 * stampTaxRate / 1000 +
                        100000 * transferFeeRate / 10000 * 2
                      ).toFixed(2)}
                    </span>
                  </div>
                </CardContent>
              </Card>

              <Button onClick={handleSaveFeeConfig} disabled={saveConfig.isPending} className="w-full">
                {saveConfig.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                保存费率配置
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== 券商账号 ===== */}
        <TabsContent value="account" className="space-y-4">
          {/* 安全提示 */}
          <Card className="border-yellow-500/30 bg-yellow-500/5">
            <CardContent className="p-4 flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-yellow-500 shrink-0 mt-0.5" />
              <div className="text-sm space-y-1">
                <p className="font-medium text-yellow-400">安全提示</p>
                <p className="text-xs text-muted-foreground">
                  您的交易密码将加密存储在服务器端，传输过程使用 HTTPS 加密。
                  建议在使用完毕后关闭「启用交易」开关。
                  在正式交易前，请先使用「模拟模式」测试策略。
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border/50">
            <CardHeader>
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Landmark className="h-4 w-4 text-primary" />
                东方财富账号
                {account && account.accountId ? (
                  <Badge variant="outline" className="text-[10px] border-green-500/50 text-green-400">
                    <ShieldCheck className="h-2.5 w-2.5 mr-1" />已配置
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-[10px]">未配置</Badge>
                )}
              </CardTitle>
              <CardDescription className="text-xs">
                请输入您的东方财富量化交易账号和密码
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs">交易账号</Label>
                  <Input
                    value={accountId}
                    onChange={(e) => setAccountId(e.target.value)}
                    placeholder="请输入东方财富账号"
                    className="h-9"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">交易密码</Label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={account?.encryptedPassword ? "已设置（留空不修改）" : "至少 8 位，建议含字母与数字"}
                    className="h-9"
                    minLength={8}
                    maxLength={64}
                  />
                  {/* [P2-64 2026-05-23] 强度提示；只在已输入但太短/缺字符类型时显示 */}
                  {password.length > 0 && password.length < 8 && (
                    <p className="text-[11px] text-orange-500">密码至少 8 位</p>
                  )}
                  {password.length >= 8 && !/[a-zA-Z]/.test(password) && (
                    <p className="text-[11px] text-amber-500">建议密码包含字母</p>
                  )}
                  {password.length >= 8 && !/\d/.test(password) && (
                    <p className="text-[11px] text-amber-500">建议密码包含数字</p>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between pt-2">
                <div>
                  <p className="text-sm font-medium">启用交易</p>
                  <p className="text-xs text-muted-foreground">
                    关闭后系统不会发送任何交易指令到券商
                  </p>
                </div>
                <Switch checked={isActive} onCheckedChange={setIsActive} />
              </div>

              <Button onClick={handleSaveBroker} disabled={saveBroker.isPending} className="w-full">
                {saveBroker.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                保存账号信息
              </Button>
            </CardContent>
          </Card>

          {/* 兴业证券 SMT-Q 账号 */}
          <Card className="bg-card border-border/50">
            <CardHeader>
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Landmark className="h-4 w-4 text-amber-400" />
                兴业证券 SMT-Q
                <Badge variant="outline" className="text-[10px] border-amber-500/50 text-amber-400">当前使用</Badge>
              </CardTitle>
              <CardDescription className="text-xs">
                通过 XtQuant 接口下单，需在 Windows 机器上登录 SMT-Q 客户端
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">资金账户号</Label>
                  <Input value={xingyeAccount} onChange={e=>setXingyeAccount(e.target.value)} placeholder="兴业证券资金账号" className="h-9 text-xs" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">状态</Label>
                  <div className="h-9 flex items-center">
                    <Badge variant="outline" className="text-[10px] border-green-500/50 text-green-400">
                      <Wifi className="h-2.5 w-2.5 mr-1" />SMT-Q 运行中（Windows）
                    </Badge>
                  </div>
                </div>
              </div>
              <p className="text-[10px] text-muted-foreground">行情源：XtQuant L1（五档，3秒快照）</p>
              <Button
                size="sm" className="w-full mt-1"
                onClick={() => saveConfig.mutate({ xyAccountId: xingyeAccount, miniQmtPath } as any)}
                disabled={saveConfig.isPending}
              >
                {saveConfig.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
                保存账号信息
              </Button>
            </CardContent>
          </Card>

          {/* 海通证券 QMT 账号 */}
          <Card className="bg-card border-border/50">
            <CardHeader>
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Landmark className="h-4 w-4 text-cyan-400" />
                海通证券 QMT
                <Badge variant="outline" className="text-[10px] border-gray-500/50 text-gray-400">待开通</Badge>
              </CardTitle>
              <CardDescription className="text-xs">
                节后开通 QMT + L2 行情权限（¥2,980/年），支持 XtQuant L2 逐笔
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">资金账户号</Label>
                  <Input value={haitongAccount} onChange={e=>setHaitongAccount(e.target.value)} placeholder="海通证券资金账号（待填写）" className="h-9 text-xs" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">QMT 安装路径</Label>
                  <Input value={haitongQmtPath} onChange={e=>setHaitongQmtPath(e.target.value)} placeholder="如：C:\htzq\userdata" className="h-9 text-xs font-mono" />
                </div>
              </div>
              <div className="flex items-start gap-2 p-3 rounded-lg bg-cyan-500/5 border border-cyan-500/20">
                <Info className="h-3.5 w-3.5 text-cyan-400 mt-0.5 shrink-0" />
                <p className="text-[11px] text-muted-foreground">
                  开通后在此填写账号，引擎将自动路由绑定海通账户的股票下单至此账户。
                  行情源自动切换为 XtQuant L2 逐笔数据。
                </p>
              </div>
              <Button variant="outline" size="sm" className="w-full" onClick={() => {
                toast.info("请先联系海通证券客户经理开通 QMT + L2 权限");
              }}>
                <Save className="h-3.5 w-3.5 mr-1.5" />保存海通账号配置
              </Button>
            </CardContent>
          </Card>

          {/* 接口对接进度 */}
          <Card className="bg-card border-border/50">
            <CardHeader>
              <CardTitle className="text-sm font-medium">接口对接进度</CardTitle>
              <CardDescription className="text-xs">
                各接口的实际对接状态和说明
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {[
                { name: "东方财富免费数据API", status: "done", desc: "资金流向、北向资金、板块排行、市场情绪、个股基本面、公告、新闻" },
                { name: "腾讯财经实时行情", status: "done", desc: "实时价格、涨跌幅、成交量，用于SSE推送和交易引擎" },
                { name: "兴业 SMT-Q 交易代理", status: "done", desc: "XtQuant Python API 封装、交易网关、行情适配器、风控引擎、Web回调已开发" },
                { name: "XtQuant L1 五档行情", status: "done", desc: "通过兴业 SMT-Q 自带的 xtdata 模块获取五档盘口行情（L1），延迟约3秒" },
                {
                  name: "XtQuant L2 逐笔行情",
                  status: (ifConfig as any)?.quoteSource === "xtquant" && (ifConfig as any)?.enableL2 ? "done" : (ifConfig as any)?.quoteSource === "xtquant" ? "mock" : "pending",
                  desc: (ifConfig as any)?.quoteSource === "xtquant"
                    ? ((ifConfig as any)?.enableL2 ? "已启用 XtQuant L2 逐笔行情，需向讯投购买权限（¥2,980/年）" : "XtQuant 行情已连接（L1），L2 逐笔需开通讯投付费权限后在此页面启用")
                    : "当前未使用 XtQuant 行情源，切换行情源为 XtQuant 后可启用 L2 逐笔"
                },
                { name: "腾讯财经 L1（备用）", status: "done", desc: "免费实时行情，网络故障时自动切换，用于策略引擎兜底" },
                { name: "EMT 量化交易终端", status: "pending", desc: "需在本地安装EMT客户端并开启API服务" },
                { name: "QMT 掘金量化平台", status: "pending", desc: "需在本地安装QMT客户端并开启API服务" },
              ].map((item) => (
                <div key={item.name} className="flex items-start gap-3 p-3 rounded-lg bg-muted/20">
                  {item.status === "done" ? (
                    <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
                  ) : item.status === "mock" ? (
                    <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
                  ) : (
                    <XCircle className="h-4 w-4 text-muted-foreground/50 shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-xs font-medium">{item.name}</p>
                      <Badge variant="outline" className={`text-[9px] ${
                        item.status === "done" ? "text-emerald-400 border-emerald-500/30" :
                        item.status === "mock" ? "text-amber-400 border-amber-500/30" :
                        "text-muted-foreground border-border"
                      }`}>
                        {item.status === "done" ? "已对接" : item.status === "mock" ? "模拟中" : "待对接"}
                      </Badge>
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-0.5">{item.desc}</p>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* 实盘模式二次确认弹窗 */}
      <AlertDialog open={showLiveConfirm} onOpenChange={setShowLiveConfirm}>
        <AlertDialogContent className={`bg-background ${pendingMode === 'live' ? 'border-red-500/30' : 'border-amber-500/30'}`}>
          <AlertDialogHeader>
            <AlertDialogTitle className={`flex items-center gap-2 ${pendingMode === 'live' ? 'text-red-400' : 'text-amber-400'}`}>
              <AlertTriangle className="h-5 w-5" />
              {pendingMode === 'live' ? '切换到实盘交易模式' : '切换到模拟交易模式'}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              {pendingMode === 'live' ? (
                <>
                  <p className="text-sm">您即将将交易引擎切换为<span className="text-red-400 font-bold">实盘交易模式</span>，切换后：</p>
                  <ul className="text-sm space-y-1.5 list-disc pl-4">
                    <li>所有交易指令将<span className="text-red-400 font-semibold">真实执行</span>，产生实际资金变动</li>
                    <li>引擎端将在下次配置热更新时自动切换（约30秒内）</li>
                    <li>请确保 miniQMT 已登录且风控参数已正确配置</li>
                  </ul>
                  <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                    <p className="text-xs text-red-300">❗ 实盘交易有风险，请确认您已充分测试策略并了解相关风险。</p>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-sm">您即将将交易引擎切换为<span className="text-amber-400 font-bold">模拟交易模式</span>，切换后：</p>
                  <ul className="text-sm space-y-1.5 list-disc pl-4">
                    <li>所有交易指令将<span className="text-amber-400 font-semibold">仅记录不实际下单</span></li>
                    <li>引擎端将在下次配置热更新时自动切换（约30秒内）</li>
                    <li><span className="text-red-400 font-semibold">当前实盘持仓不会自动平仓</span>，请在切换前手动处理持仓</li>
                  </ul>
                  <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                    <p className="text-xs text-amber-300">⚠️ 切换到模拟模式后，实盘持仓将不再受引擎管理，请确保已妥善处理所有持仓。</p>
                  </div>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setShowLiveConfirm(false); setPendingMode(null); }}>
              取消
            </AlertDialogCancel>
            <AlertDialogAction
              className={pendingMode === 'live' ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-amber-600 hover:bg-amber-700 text-white'}
              onClick={() => {
                if (pendingMode) {
                  setTradingProxyMode(pendingMode);
                  setHasExplicitlyChangedMode(true);
                  toast.warning(pendingMode === 'live' ? '已切换为实盘模式，请保存配置后生效' : '已切换为模拟模式，请保存配置后生效');
                }
                setShowLiveConfirm(false);
                setPendingMode(null);
              }}
            >
              {pendingMode === 'live' ? '确认切换实盘' : '确认切换模拟'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
